import React, {FC, useEffect, useRef, useState} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  usePrevious,
  useTimeout,
} from '../../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {DataEventActionsEnum} from '../../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {ViewerDataEventObject} from '../../../../../../../libs/shared/core/src/models/tracking/objectNames/viewerDataEventObject';
import {useDataEventTracking} from '../../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {CellsContext} from '../../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Icon} from '../../../../../../../libs/shared/ui/src/components/icon/icon';
import {tooltipDefaultDelay} from '../../../../../../../libs/shared/ui/src/components/tooltips/tooltipHelpers';
import {fontSizes, fontWeights} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ConversationModel} from '../../../../../../models/api/conversationModel';
import {useSimilarConversations} from '../../hooks/useSimilarConversations';
import {StyledPopoverCoordinatorWrapperDiv} from '../conversationHeaderPill';
import {ConversationHeaderAiSimilarDropdown} from './conversationHeaderAiSimilarDropdown';

/*
 * Props.
 */

interface ConversationHeaderAiSimilarButtonProps {
  conversation: ConversationModel;
  cellsContext: CellsContext;
  panelId: ViewerPanelId;
}

/*
 * Style.
 */

const StyledSimilarConversationsWrapperDiv = styled.div<{$hasHover: boolean}>`
  border-radius: 100px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 1.5px 8px;
  height: 20px;
  width: max-content;
  font-size: ${fontSizes.verySmall};

  margin-left: 1px;
  border: ${(p) => p.theme.greys.shade70} 1px solid;
  color: ${(p) => p.theme.greys.shade70};
  font-weight: ${fontWeights.semibold};
  background-color: ${(p) => p.theme.greys.shade10};

  &:hover {
    background-color: ${(p) => p.theme.greys.shade30};
  }
  ${(p) =>
    p.$hasHover &&
    css`
      background-color: ${p.theme.greys.shade30};
    `}
`;

/*
 * Component.
 */

export const ConversationHeaderAiSimilarButton: FC<ConversationHeaderAiSimilarButtonProps> = (props) => {
  const {conversation} = props;
  const anchor = useRef<HTMLDivElement>(null);
  const [shouldRenderDropdown, setShouldRenderDropdown] = useState(false);
  const [safeSetTimeout, safeClearTimeout] = useTimeout();
  const {trackDataEvent} = useDataEventTracking();

  const {conversationIds, isLoading, isLoaded, isErred, isEmpty} = useSimilarConversations(conversation);
  const previousIsLoading = usePrevious(isLoading);

  useEffect(() => {
    // If we finished loading and we have similar conversations, track the view event.
    if (!isLoading && previousIsLoading && conversationIds.length !== 0) {
      trackDataEvent(DataEventActionsEnum.VIEW, ViewerDataEventObject.SIMILAR_CONVERSATION_PILL);
    }
  }, [conversationIds, isLoading, previousIsLoading, trackDataEvent]);

  const onMouseEnter = () => {
    if (!isLoaded) {
      return;
    }

    safeSetTimeout(() => {
      performance.mark(`conversation_header_similar_pill_${conversation.id}_hover_start`);
      setShouldRenderDropdown(true);
    }, tooltipDefaultDelay);
  };

  const onMouseLeave = () => {
    if (!isLoaded) {
      return;
    }

    safeClearTimeout();
    setShouldRenderDropdown(false);

    // Make sure the mark exists since it is set in the timeout when it is opened.
    if (
      performance.getEntriesByName(`conversation_header_similar_pill_${conversation.id}_hover_start`)
        .length === 0
    ) {
      return;
    }
    const hoverDuration = performance.measure(
      `conversation_header_similar_pill_${conversation.id}_hover`,
      `conversation_header_similar_pill_${conversation.id}_hover_start`,
    );
    performance.clearMarks(`conversation_header_similar_pill_${conversation.id}_hover_start`);
    performance.clearMeasures(`conversation_header_similar_pill_${conversation.id}_hover`);
    trackDataEvent(DataEventActionsEnum.HOVER, ViewerDataEventObject.SIMILAR_CONVERSATION_PILL, {
      hover_time: Math.round(hoverDuration.duration),
    });
  };

  if (isEmpty || isErred || isLoading) {
    return null;
  }

  return (
    <StyledPopoverCoordinatorWrapperDiv onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <StyledSimilarConversationsWrapperDiv
        ref={anchor}
        $hasHover={shouldRenderDropdown}
        data-testid="similar-conversations-button"
      >
        <Icon name="sparklesAiFilledTiny" />
        {isLoaded && (
          <Trans id="conversation-header-ai-similar-conversations">{conversationIds.length} similar</Trans>
        )}
      </StyledSimilarConversationsWrapperDiv>
      {shouldRenderDropdown && (
        <ConversationHeaderAiSimilarDropdown
          anchor={anchor}
          conversationIds={conversationIds}
          cellsContext={props.cellsContext}
          panelId={props.panelId}
        />
      )}
    </StyledPopoverCoordinatorWrapperDiv>
  );
};
